import type { ListDiscountsResponse, ProDiscount } from "@wallyo/contracts/console/billing";
import { t } from "../../i18n";
import { formatNumber } from "../../i18n/format";
import {
  canEndEarly,
  discountPlanLabel,
  discountTarget,
  percentOffLabel,
  periodsLabel,
  promoStateName,
  windowLabel,
  PROMO_STATE_TONE,
  type BillingListState,
} from "./billing-model";
import { FailedCard, LoadingCard, RefreshBanner } from "./BillingNotices";

export type DiscountsViewProps = {
  state: BillingListState<ListDiscountsResponse>;
  canOfferDiscounts: boolean;
  onNewDiscount: () => void;
  onEndDiscount: (discount: ProDiscount) => void;
  onRetry: () => void;
};

/**
 * Remises: a percentage off for one pro, a country or a trade. A pro gets the
 * single lowest price: a remise and a promo never add up (ADR-0018 §4a).
 */
export function DiscountsView({ state, canOfferDiscounts, onNewDiscount, onEndDiscount, onRetry }: DiscountsViewProps) {
  if (!state.data && state.status === "failed") {
    return <FailedCard text={t("billing.discounts.loadFailed")} error={state.error} onRetry={onRetry} />;
  }
  if (!state.data) return <LoadingCard text={t("billing.discounts.loading")} />;

  const { discounts } = state.data;
  return (
    <div className="card">
      <RefreshBanner show={state.status === "failed"} onRetry={onRetry} />
      <p className="foot">{t("billing.discounts.intro")}</p>
      <NewDiscountButton show={canOfferDiscounts} onNewDiscount={onNewDiscount} />
      <Empty show={discounts.length === 0} />
      <DiscountTable discounts={discounts} canOfferDiscounts={canOfferDiscounts} onEndDiscount={onEndDiscount} />
    </div>
  );
}

function NewDiscountButton({ show, onNewDiscount }: { show: boolean; onNewDiscount: () => void }) {
  if (!show) return null;
  return (
    <div className="btns">
      <button className="btn primary" type="button" data-action="newDiscount" onClick={onNewDiscount}>
        {t("billing.action.newDiscount")}
      </button>
    </div>
  );
}

function Empty({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <p className="foot" role="status" data-empty="discounts">
      {t("billing.discounts.empty")}
    </p>
  );
}

function DiscountTable({
  discounts,
  canOfferDiscounts,
  onEndDiscount,
}: {
  discounts: readonly ProDiscount[];
  canOfferDiscounts: boolean;
  onEndDiscount: (discount: ProDiscount) => void;
}) {
  if (discounts.length === 0) return null;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>{t("billing.col.name")}</th>
            <th>{t("billing.col.who")}</th>
            <th>{t("billing.col.plan")}</th>
            <th className="num">{t("billing.col.percentOff")}</th>
            <th>{t("billing.col.periods")}</th>
            <th>{t("billing.col.window")}</th>
            <th>{t("billing.col.state")}</th>
            <th className="num">{t("billing.col.subscribers")}</th>
            <th>{t("billing.col.createdBy")}</th>
            <th aria-label={t("billing.col.actions")} />
          </tr>
        </thead>
        <tbody>
          {discounts.map((discount) => (
            <DiscountRow
              key={discount.proDiscountId}
              discount={discount}
              canOfferDiscounts={canOfferDiscounts}
              onEndDiscount={onEndDiscount}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DiscountRow({
  discount,
  canOfferDiscounts,
  onEndDiscount,
}: {
  discount: ProDiscount;
  canOfferDiscounts: boolean;
  onEndDiscount: (discount: ProDiscount) => void;
}) {
  return (
    <tr data-discount={discount.proDiscountId} data-state={discount.state}>
      <td>
        <bdi>{discount.name}</bdi>
        <br />
        <span className="meta">
          <bdi>{discount.reason}</bdi>
        </span>
      </td>
      <td data-scope={discount.scope}>
        <bdi>{discountTarget(discount)}</bdi>
        <br />
        <span className="meta">{t(`billing.scope.${discount.scope}` as const)}</span>
      </td>
      <td>{discountPlanLabel(discount)}</td>
      <td className="num">{percentOffLabel(discount.percentOff)}</td>
      <td>{periodsLabel(discount.periods)}</td>
      <td className="nowrap">{windowLabel(discount)}</td>
      <td>
        <span className={`pill ${PROMO_STATE_TONE[discount.state]}`}>{promoStateName(discount.state)}</span>
      </td>
      <td className="num" data-subscribers="">
        {formatNumber(discount.subscriberCount)}
      </td>
      <td>
        <bdi>{discount.createdBy}</bdi>
      </td>
      <td className="row-actions">
        <EndButton discount={discount} canOfferDiscounts={canOfferDiscounts} onEndDiscount={onEndDiscount} />
      </td>
    </tr>
  );
}

function EndButton({
  discount,
  canOfferDiscounts,
  onEndDiscount,
}: {
  discount: ProDiscount;
  canOfferDiscounts: boolean;
  onEndDiscount: (discount: ProDiscount) => void;
}) {
  if (!canOfferDiscounts || !canEndEarly(discount)) return null;
  return (
    <button
      className="btn small"
      type="button"
      data-end={discount.proDiscountId}
      onClick={() => onEndDiscount(discount)}
    >
      {t("billing.action.endNow")}
    </button>
  );
}
